import React, { useEffect, useState, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { api, formatCurrency, formatDate } from '@/lib/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel } from '@/components/ui/dropdown-menu';
import { FileDown, ChevronLeft, Pencil, FileSignature as SignIcon, Mail, CheckCircle2, Sparkles, Zap, Battery, Loader2 } from 'lucide-react';
import { SignaturePad } from '@/components/SignaturePad';

// Categorie cross-sell dopo firma di un contratto FV
const CROSS_SELL_OPTIONS = [
  { key: 'wallbox', label: 'Wallbox (colonnina EV)', icon: Zap },
  { key: 'accumulo', label: 'Sistema di Accumulo', icon: Battery },
  { key: 'climatizzazione', label: 'Climatizzazione / Pompa di calore', icon: Sparkles },
];

const hasFvProduct = (contract) => {
  const prods = contract?.products || [];
  return prods.some(p => (p.category || '').toLowerCase().startsWith('fotovoltaico'));
};

const alreadyHasCrossSell = (contract, key) => {
  const prods = contract?.products || [];
  const k = key.toLowerCase();
  return prods.some(p => (p.category || '').toLowerCase().includes(k));
};

export default function ContractDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [contract, setContract] = useState(null);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [customerSig, setCustomerSig] = useState('');
  const [companySig, setCompanySig] = useState('');
  const customerRef = useRef(null);
  const companyRef = useRef(null);
  const [generatingPdf, setGeneratingPdf] = useState({}); // { [mode]: true }
  const generatePdf = async (mode) => {
    if (generatingPdf[mode]) return;
    setGeneratingPdf(prev => ({ ...prev, [mode]: true }));
    const labelMap = { blank: 'PDF contratto stampabile vuoto', filled: 'PDF compilato', signed: 'PDF firmato' };
    const tId = toast.loading(`Generazione ${labelMap[mode] || 'PDF'} in corso, attendere…`);
    try {
      await api.openContractPdf(id, mode);
      toast.success('PDF generato', { id: tId });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore generazione PDF', { id: tId });
    } finally {
      setGeneratingPdf(prev => { const n = { ...prev }; delete n[mode]; return n; });
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const c = await api.getContract(id);
      setContract(c);
      const comp = await api.getCompany(c.company_id);
      setCompany(comp);
      setCustomerSig(c.signature?.customer_signature_data_url || '');
      setCompanySig(c.signature?.company_signature_data_url || '');
    } catch (e) { toast.error('Contratto non trovato'); navigate('/contracts'); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [id]);

  const handleSign = async () => {
    const custUrl = customerRef.current?.getDataUrl() || customerSig;
    if (!custUrl) { toast.error('Inserisci almeno la firma del cliente'); return; }
    try {
      const compUrl = companyRef.current?.getDataUrl() || companySig;
      await api.signContract(id, {
        customer_signature_data_url: custUrl,
        company_signature_data_url: compUrl || '',
        signed_place: contract.contract?.place || '',
      });
      toast.success('Contratto firmato');
      load();
    } catch (e) { toast.error('Errore firma'); }
  };

  if (loading || !contract) return <div className="p-10 text-center text-muted-foreground">Caricamento...</div>;

  const sigPresent = !!contract.signature?.customer_signature_data_url;

  return (
    <div>
      <PageHeader
        title={<span className="font-mono">{contract.contract?.number || 'Contratto'}</span>}
        description={<>Cliente: <strong>{contract.customer?.first_name} {contract.customer?.last_name}</strong> &middot; Partner: {company?.name}</>}
        actions={
          <div className="flex gap-2">
            <Button asChild variant="outline" size="sm"><Link to="/contracts"><ChevronLeft className="size-4 mr-1" /> Indietro</Link></Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild><Button data-testid="pdf-download-menu"><FileDown className="size-4 mr-2" />Scarica PDF</Button></DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel className="text-xs">Modalità</DropdownMenuLabel>
                <DropdownMenuItem onSelect={(e) => e.preventDefault()} disabled={!!generatingPdf.blank} onClick={() => generatePdf('blank')} data-testid="pdf-download-blank">
                  {generatingPdf.blank ? <Loader2 className="size-4 mr-2 animate-spin" /> : <FileDown className="size-4 mr-2" />}
                  {generatingPdf.blank ? 'Generazione in corso…' : 'PDF Contratto Stampabile Vuoto'}
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={(e) => e.preventDefault()} disabled={!!generatingPdf.filled} onClick={() => generatePdf('filled')} data-testid="pdf-download-filled">
                  {generatingPdf.filled ? <Loader2 className="size-4 mr-2 animate-spin" /> : <FileDown className="size-4 mr-2" />}
                  {generatingPdf.filled ? 'Generazione in corso…' : 'Compilato digitalmente'}
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={(e) => e.preventDefault()} disabled={!!generatingPdf.signed} onClick={() => generatePdf('signed')} data-testid="pdf-download-signed">
                  {generatingPdf.signed ? <Loader2 className="size-4 mr-2 animate-spin" /> : <FileDown className="size-4 mr-2" />}
                  {generatingPdf.signed ? 'Generazione in corso…' : 'Firmato (con firma digitale)'}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {(contract.status === 'firmato' || sigPresent) && hasFvProduct(contract) && contract.customer_id && (
            <Card className="border-primary/40 bg-gradient-to-r from-primary/10 to-primary/5" data-testid="cross-sell-banner">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Sparkles className="size-4 text-primary" />
                  Suggerimento cross-sell
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-3">
                  Il cliente ha firmato un impianto fotovoltaico. Proponi un preventivo aggiuntivo per completare l&apos;offerta di efficientamento energetico.
                </p>
                <div className="flex flex-wrap gap-2">
                  {CROSS_SELL_OPTIONS.map(opt => {
                    const Icon = opt.icon;
                    const already = alreadyHasCrossSell(contract, opt.key);
                    return (
                      <Button
                        key={opt.key}
                        variant={already ? 'ghost' : 'outline'}
                        size="sm"
                        disabled={already}
                        onClick={() => navigate(`/contracts/new?customerId=${contract.customer_id}&mode=quote&suggestCategory=${opt.key}`)}
                        data-testid={`cross-sell-${opt.key}`}
                      >
                        <Icon className="size-4 mr-1.5" />
                        {opt.label}
                        {already && <Badge variant="secondary" className="ml-2 text-[10px]">già incluso</Badge>}
                      </Button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}
          <Card>
            <CardHeader className="pb-2 flex flex-row items-center justify-between">
              <CardTitle className="text-base">Riepilogo</CardTitle>
              <Badge variant={sigPresent ? 'default' : 'outline'} className="capitalize">{contract.status}</Badge>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                <div><div className="text-xs text-muted-foreground">Data</div><div className="font-medium">{formatDate(contract.contract?.date)}</div></div>
                <div><div className="text-xs text-muted-foreground">Luogo</div><div className="font-medium">{contract.contract?.place || '—'}</div></div>
                <div><div className="text-xs text-muted-foreground">Cliente</div><div className="font-medium">{contract.customer?.first_name} {contract.customer?.last_name}</div></div>
                <div><div className="text-xs text-muted-foreground">CF</div><div className="font-mono text-xs">{contract.customer?.fiscal_code}</div></div>
                <div><div className="text-xs text-muted-foreground">Email</div><div>{contract.customer?.email || '—'}</div></div>
                <div><div className="text-xs text-muted-foreground">Telefono</div><div>{contract.customer?.phone || '—'}</div></div>
              </div>
              <Separator />
              <div>
                <div className="text-xs text-muted-foreground mb-2">Prodotti</div>
                <div className="divide-y rounded-lg border">
                  {(contract.products || []).map((p, i) => (
                    <div key={i} className="flex justify-between gap-2 p-3 text-sm">
                      <div><div className="font-medium">{p.brand} {p.model}</div><div className="text-xs text-muted-foreground">{p.description}</div></div>
                      <div className="text-right"><div className="tabular-nums">{p.quantity} × {formatCurrency(p.unit_price)}</div><div className="font-medium tabular-nums">{formatCurrency((p.quantity || 0) * (p.unit_price || 0))}</div></div>
                    </div>
                  ))}
                </div>
              </div>
              <Separator />
              <div className="flex justify-end">
                <div className="w-full max-w-xs space-y-1 text-sm">
                  <div className="flex justify-between"><span className="text-muted-foreground">Imponibile</span><span className="tabular-nums">{formatCurrency(contract.totals?.subtotal)}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">IVA {contract.vat_rate}%</span><span className="tabular-nums">{formatCurrency(contract.totals?.vat_amount)}</span></div>
                  <Separator />
                  <div className="flex justify-between text-base font-semibold"><span>Totale</span><span className="tabular-nums">{formatCurrency(contract.totals?.total)}</span></div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Firma digitale</CardTitle></CardHeader>
            <CardContent className="space-y-6">
              <div>
                <div className="text-sm font-medium mb-2">Firma del Cliente {sigPresent && <Badge className="ml-2"><CheckCircle2 className="size-3 mr-1" />Acquisita</Badge>}</div>
                <SignaturePad ref={customerRef} value={customerSig} onChange={setCustomerSig} />
              </div>
              <div>
                <div className="text-sm font-medium mb-2">Firma del Fornitore (opzionale)</div>
                <SignaturePad ref={companyRef} value={companySig} onChange={setCompanySig} />
              </div>
              <div className="flex justify-end gap-2">
                <Button onClick={handleSign} data-testid="contract-sign-button"><SignIcon className="size-4 mr-2" />Salva firme</Button>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Partner</CardTitle></CardHeader>
            <CardContent className="text-sm space-y-2">
              <div className="flex items-center gap-3">
                {company?.logo_data_url ? <img src={company.logo_data_url} alt="" className="size-12 rounded border bg-white object-contain p-1" /> : <div className="size-12 rounded border bg-secondary" />}
                <div><div className="font-medium">{company?.name}</div><div className="text-xs text-muted-foreground">{company?.city}</div></div>
              </div>
              <div className="flex gap-1 mt-2">
                <span className="inline-block size-5 rounded border" style={{ background: company?.primary_color }} />
                <span className="inline-block size-5 rounded border" style={{ background: company?.accent_color }} />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Pagamento</CardTitle></CardHeader>
            <CardContent className="text-sm space-y-1">
              <div><span className="text-muted-foreground">Metodo:</span> <span className="font-medium capitalize">{(contract.payment?.method || '').replace(/_/g, ' ')}</span></div>
              {contract.payment?.financing_company && <div><span className="text-muted-foreground">Finanziaria:</span> {contract.payment.financing_company}</div>}
              {!!contract.payment?.deposit_percent && <div><span className="text-muted-foreground">{contract.payment?.method === 'doppio_acconto_saldo' ? '1° Acconto' : 'Acconto'}:</span> {contract.payment.deposit_percent}%</div>}
              {!!contract.payment?.second_deposit_percent && <div><span className="text-muted-foreground">2° Acconto:</span> {contract.payment.second_deposit_percent}%</div>}
              {!!contract.payment?.balance_percent && <div><span className="text-muted-foreground">Saldo:</span> {contract.payment.balance_percent}%</div>}
              {contract.payment?.notes && <div className="text-xs text-muted-foreground mt-2">{contract.payment.notes}</div>}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Immobile</CardTitle></CardHeader>
            <CardContent className="text-sm space-y-1">
              <div>{contract.property?.address}</div>
              <div className="text-muted-foreground">{contract.property?.city} ({contract.property?.province})</div>
              {contract.property?.cadastral_sheet && <div className="text-xs text-muted-foreground mt-2 font-mono">Foglio {contract.property.cadastral_sheet} · Part. {contract.property.cadastral_parcel} · Sub. {contract.property.cadastral_sub}</div>}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
